'use client';

import { usePathname } from 'next/navigation';
import { type Locale } from '@/lib/i18n';

interface PreloadImagesProps {
  locale: Locale;
}

/**
 * PreloadImages Component
 * Preloads critical above-the-fold images based on current route
 * This prevents visible loading/layout shift for users
 */
export function PreloadImages({ locale }: PreloadImagesProps) {
  const pathname = usePathname();
  
  // Determine which images to preload based on current path
  // Note: Menu images are preloaded server-side in layout.tsx for instant availability
  const getPreloadImages = () => {
    const images: string[] = [];
    
    // Homepage - video poster
    if (pathname === '/' || pathname === `/${locale}`) {
      images.push('/videos/posters/eazie-jamezz.webp');
    }
    
    // All You Can Eat page - hero images (ayce-qr-ordering-shabu-shabu-menu-1.webp is already in menu preload)
    if (pathname.includes('/all-you-can-eat')) {
      images.push(
        '/images/ayce-case-studies/ayce-tablet-ordering-shabu-shabu-menu-1.webp',
        '/images/ayce-case-studies/ayce-qr-ordering-okinii-menu-1.webp',
        '/images/ayce-case-studies/ayce-tablet-ordering-okinii-menu-1.webp'
      );
    }
    
    return images;
  };
  
  const imagesToPreload = getPreloadImages();
  
  if (imagesToPreload.length === 0) {
    return null;
  }
  
  return (
    <>
      {imagesToPreload.map((src) => (
        <link
          key={src}
          rel="preload"
          as="image"
          href={src}
          // @ts-ignore - imageSrcSet is valid but not in types
          imageSrcSet={`${src} 1x`}
        />
      ))}
    </>
  );
}

